import React, { useState } from 'react';
import { Button } from './button';
import { Input } from './input';
import { Label } from './label';

interface OnboardingWizardProps {
  onLoginRequest: () => void; // Function to start the login process
  onCompleteNewOrg: (companyName: string, userName: string) => void; // Function after new org data is filled in
}

export const OnboardingWizard: React.FC<OnboardingWizardProps> = ({
  onLoginRequest,
  onCompleteNewOrg,
}) => {
  // We'll keep step state in case we want to add organization creation back in later
  const [step, setStep] = useState(1);
  const [companyName, setCompanyName] = useState('');
  const [userName, setUserName] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleNewOrgSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (companyName && userName) {
      onCompleteNewOrg(companyName, userName);
    }
  };

  const handleLoginClick = () => {
    setIsLoading(true);
    // Call the login request function
    onLoginRequest();
    // Note: typically we would handle the end of loading in a callback,
    // but since this redirects to another page, we don't need to set loading back to false
  };

  // Handler voor bestaande organisatie
  const handleExistingOrg = () => {
    // Redirect naar Onespace login
    const apiUrl = import.meta.env.VITE_API_URL || "https://onespace-app.test";
    window.location.href = `${apiUrl}/initiate-login?callback_url=${encodeURIComponent(window.location.origin + "/index.html")}`;
  };

  return (
    <div className="fixed inset-0 bg-gray-300 backdrop-blur-sm z-50 flex items-center justify-center">
      <div className="bg-white p-8 rounded-lg shadow-xl max-w-md w-full text-gray-900">
        {step === 1 && (
          <>
            <div className="flex justify-center mb-6">
              <img src="/logo512.png" alt="Onespace Logo" className="w-16 h-16" />
            </div>
            <h2 className="text-2xl font-semibold mb-6 text-center">Welkom bij Onespace</h2>
            <div className="flex flex-col items-center">
              <Button 
                className="font-bold w-full bg-gray-800 text-white"
                onClick={handleLoginClick}
                loading={isLoading}
              >
                Inloggen of registreren
              </Button>
              <p className="text-xs text-gray-500 mt-4">
                API: {import.meta.env.VITE_API_URL}
              </p>
            </div>
          </>
        )}

        {/* Step 2 content - hidden for now but kept in case we need to reintroduce it */}
        {step === 2 && (
          <>
            <h2 className="text-2xl font-semibold mb-6 text-center">Nieuwe Organisatie Starten</h2>
            <form onSubmit={handleNewOrgSubmit} className="space-y-6">
              <div>
                <Label htmlFor="companyName" className="mb-2 block">Bedrijfsnaam</Label>
                <Input
                  id="companyName"
                  type="text"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  placeholder="Jouw Bedrijfsnaam B.V."
                  required
                />
              </div>
              <div>
                <Label htmlFor="userName" className="mb-2 block">Jouw Naam</Label>
                <Input
                  id="userName"
                  type="text"
                  value={userName}
                  onChange={(e) => setUserName(e.target.value)}
                  placeholder="Voornaam Achternaam"
                  required
                />
              </div>
              <Button type="submit" className="w-full py-3 text-lg">
                Let's Go!
              </Button>
              <Button 
                variant="link"
                className="w-full mt-2 text-muted-foreground"
                onClick={() => setStep(1)}
              >
                Terug
              </Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}; 